import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from "@angular/common/http";
import { ToastrService } from "ngx-toastr";

import { Observable, EMPTY, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { HelperService } from "../helper.service";
import { environment } from "./../../../../environments/environment";
import { APIUrlModel } from "../../model/apiurl.model";
import { AuthenticationService } from "@app/auth/services/authentication.service";

@Injectable({
  providedIn: "root",
})
export class APIRequestService {
  // A list of known error strings that might appear in a 200 OK response body
  private gatewayErrorSignatures = [
    'session timeout',
    'communicating backend service',
    'invalid token'
  ];

  longinURL = "/auth/signin";
  constructor(
    private httpClient: HttpClient,
    private helperService: HelperService,
    private toasterService: ToastrService,
    private authenticationService: AuthenticationService
  ) {}

  getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = this.helperService.token.get();
    headers = headers.append("Content-Type", "application/json");

    // Add custom headers for analytics and security
    headers = headers.append('X-User-Agent', navigator.userAgent);
    headers = headers.append('X-Client-IP', 'N/A (Retrieve on Gateway)');
    headers = headers.append('X-Client-Location', 'N/A (Requires User Permission)');
    headers = headers.append('X-Mac-Address', 'N/A (Not Accessible)');

    if (token !== null) {
      headers = headers.append("Authorization", "Bearer " + token);
    }
    return headers;
  }

  getUploadHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = this.helperService.token.get();
    headers.set("Content-Type", null);
    headers.set("Accept", "multipart/form-data");

    // Add custom headers for analytics and security
    headers = headers.append('X-User-Agent', navigator.userAgent);
    headers = headers.append('X-Client-IP', 'N/A (Retrieve on Gateway)');
    headers = headers.append('X-Client-Location', 'N/A (Requires User Permission)');
    headers = headers.append('X-Mac-Address', 'N/A (Not Accessible)');

    if (token !== null) {
      headers = headers.append("Authorization", "Bearer " + token);
    }
    return headers;
  }

  getUploadHeadersForFileUpload(realfile?): HttpHeaders {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'multipart/form-data');
    if(realfile){
        headers.set('Content-Length', realfile.size);
    }
    headers.set('Accept', 'multipart/form-data');
    const token = this.helperService.token.get();
    if (token !== null) {
        headers = headers.append('Authorization', 'Bearer ' + token);
    }
    return headers;
} 

  get(
    url: string,
    urlParams?: HttpParams,
    urlModel?: APIUrlModel,
    extraHeaders?: { [name: string]: string }
  ): Observable<any> {
    console.log('%c[RCA] get() method called with:', 'color: #FF6B35; font-weight: bold;', { url, urlParams, urlModel });
    
    if (urlModel) {
      url = this.getURL(urlModel);
    } else {
      console.log(`%c[Direct API Call] %c=> ${url}`, 'color: #0000FF; font-weight: bold;', 'color: #444;');
    }
    
    console.log('%c[RCA] About to make HTTP GET call to:', 'color: #FF6B35;', url);
    console.log('%c[RCA] Headers being sent:', 'color: #FF6B35;', this.getHeaders());
    
    let headers = this.getHeaders();
    Object.keys(extraHeaders || {}).forEach(name => headers = headers.set(name, extraHeaders[name]));
    const httpCall = this.httpClient.get(url, { params: urlParams, headers });
    
    console.log('%c[RCA] HTTP call created, subscribing with pipe...', 'color: #FF6B35;');
    
    return httpCall.pipe(
      map(response => this.checkForGatewayErrors(response)),
      catchError((err) => {
        // Error is caught here, but we will let the ErrorInterceptor handle the user notification.
        // We simply re-throw the error to be caught by the global handler.
        return throwError(err);
      })
    );
  }

  getPdf(
    url: string,
    urlParams?: HttpParams,
    urlModel?: APIUrlModel
  ): Observable<Blob> {
    if (urlModel) {
      url = this.getURL(urlModel);
    } else {
      console.log(`%c[Direct API Call] %c=> ${url}`, 'color: #0000FF; font-weight: bold;', 'color: #444;');
    }
    return this.httpClient.get(url, { responseType: "blob", headers: this.getHeaders() });
  }

  
  postPdf(url: string, body: object, urlModel?: APIUrlModel): Observable<Blob> {
    if (urlModel) {
        url = this.getURL(urlModel);
    }
    return this.httpClient.post(url, JSON.stringify(body), {
        responseType: 'blob',
        headers: this.getHeaders()
    }).pipe(
        catchError(err => {
            if (err.status === 401) {
                this.authenticationService.logout();
                return EMPTY;
            } else {
                const ermsg = 'Error downloading file. Please try again.';
                this.toasterService.error(ermsg);
                return throwError(err);
            }
        })
    );
}

  postMultipart(actionUrl: string, realfile: File, data: FormData, urlModel?: APIUrlModel): Observable<any> {
    if (urlModel) {
        actionUrl = this.getURL(urlModel);
    }
    
    const url = actionUrl;
    
    // Specify responseType: 'text' to prevent JSON parsing issues
    return this.httpClient.post(url, data, { 
        headers: this.getUploadHeadersForFileUpload(realfile),
        responseType: 'text'  // Add this line to handle empty or non-JSON responses
    }).pipe(
        map(response => {
            // If response is empty or just whitespace, return an empty object
            if (!response || response.trim() === '') {
                return {};
            }
            
            // Try to parse as JSON if it's actually JSON
            try {
                return JSON.parse(response);
            } catch (e) {
                // If it can't be parsed as JSON, just return the text
                return response;
            }
        }),
        catchError(err => {
            if (err.status === 401) {
                this.authenticationService.logout();
                return EMPTY;
            } else {
                const ermsg = (err.error) ? (err.error['error-message']) : 'Something went wrong.';
                this.toasterService.error(ermsg);
                return throwError(err);
            }
        })
    );
}

  getBlob(
    url: string,
    urlParams?: HttpParams,
    urlModel?: APIUrlModel
  ): Observable<Blob> {
    if (urlModel) {
      url = this.getURL(urlModel);
    } else {
      console.log(`%c[Direct API Call] %c=> ${url}`, 'color: #0000FF; font-weight: bold;', 'color: #444;');
    }
    return this.httpClient.get(url, { responseType: "blob", headers: this.getHeaders() });
  }


  getBlobWithParams(
    url: string,
    urlParams?: HttpParams,
    urlModel?: APIUrlModel
  ): Observable<Blob> {
    if (urlModel) {
      url = this.getURL(urlModel);
    } else {
      console.log(
        `%c[Direct API Call] %c=> ${url}`,
        'color: #0000FF; font-weight: bold;',
        'color: #444;'
      );
    }
  
    return this.httpClient.get(url, {
      responseType: "blob",
      headers: this.getHeaders(),
      params: urlParams // ✅ Pass query params here
    });
  }
  

  post(url: string, body: object, urlModel?: APIUrlModel): Observable<any> {
    if (urlModel) {
      url = this.getURL(urlModel);
    } else {
      console.log(`%c[Direct API Call] %c=> ${url}`, 'color: #0000FF; font-weight: bold;', 'color: #444;');
    }
    return this.httpClient.post(url, JSON.stringify(body), { headers: this.getHeaders() }).pipe(
      map(response => this.checkForGatewayErrors(response)),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  upload(url: string, body: object): Observable<any> {
    console.log(`%c[Direct API Call] %c=> ${url}`, 'color: #0000FF; font-weight: bold;', 'color: #444;');
    return this.httpClient
      .post(url, body, { headers: this.getUploadHeaders() })
      .pipe(
        map(response => this.checkForGatewayErrors(response)),
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  put(url: string, body: object, urlModel?: APIUrlModel): Observable<any> {
    if (urlModel) {
      url = this.getURL(urlModel);
    } else {
      console.log(`%c[Direct API Call] %c=> ${url}`, 'color: #0000FF; font-weight: bold;', 'color: #444;');
    }
    return this.httpClient.put(url, JSON.stringify(body), { headers: this.getHeaders() }).pipe(
      map(response => this.checkForGatewayErrors(response)),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  delete(url: string, urlModel?: APIUrlModel): Observable<any> {
    if (urlModel) {
      url = this.getURL(urlModel);
    } else {
      console.log(`%c[Direct API Call] %c=> ${url}`, 'color: #0000FF; font-weight: bold;', 'color: #444;');
    }
    return this.httpClient.delete(url, { headers: this.getHeaders() }).pipe(
      map(response => this.checkForGatewayErrors(response)),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  private checkForGatewayErrors(response: any): any {
    if (response && typeof response === 'object') {
      // Convert response to string to check for error signatures
      const responseString = JSON.stringify(response).toLowerCase();

      const hasError = this.gatewayErrorSignatures.some(signature => responseString.includes(signature));

      if (hasError) {
        console.error('Gateway error detected in 200 OK response:', response);
        // Throw an error to be caught by the catchError operator and handled by the ErrorInterceptor
        throw new HttpErrorResponse({ 
          error: { message: 'The server returned an invalid response.' },
          status: 401, // Treat this as an unauthorized error to trigger a logout
          statusText: 'Invalid Response from Gateway'
        });
      }
    }
    return response;
  }

  private getURL(urlobj: APIUrlModel) {
    console.log('%c[RCA] getURL() called with urlobj:', 'color: #FF6B35; font-weight: bold;', urlobj);
    
    const keycloakSessionActive = this.helperService.keycloakSession.get();
    const embeddedModeActive = this.helperService.embeddedMode.get();
    const shouldUseGateway = keycloakSessionActive || embeddedModeActive;
    console.log('%c[RCA] Keycloak session check result:', 'color: #FF6B35;', keycloakSessionActive);
    console.log('%c[RCA] Embedded mode check result:', 'color: #FF6B35;', embeddedModeActive);
    
    if (shouldUseGateway) {
      console.log('Gateway Debug: urlobj:', urlobj);
      console.log('Gateway Debug: environment.gatewayUrl:', environment.gatewayUrl);
      console.log('Gateway Debug: environment.assayingPath:', environment.assayingPath);
      console.log('Gateway Debug: urlobj.part:', urlobj.part);
      
      console.log('Gateway Debug: About to process gatewayUrl...');
      const gatewayUrl = environment.gatewayUrl.endsWith('/') ? environment.gatewayUrl.slice(0, -1) : environment.gatewayUrl;
      console.log('Gateway Debug: gatewayUrl processed:', gatewayUrl);
      
      console.log('Gateway Debug: About to process assayingPath...');
      const assayingPath = environment.assayingPath.startsWith('/') ? environment.assayingPath.substring(1) : environment.assayingPath;
      console.log('Gateway Debug: assayingPath processed:', assayingPath);
      
      console.log('Gateway Debug: About to process apiPart...');
      const apiPart = urlobj.part.startsWith('/') ? urlobj.part.substring(1) : urlobj.part;
      console.log('Gateway Debug: apiPart processed:', apiPart);
      
      const finalUrl = `${gatewayUrl}/${assayingPath}/${apiPart}`;
      console.log(`%c[API Gateway Call] %c=> ${finalUrl}`, 'color: #8A2BE2; font-weight: bold;', 'color: #444;');
      console.log('%c[RCA] Returning gateway URL:', 'color: #FF6B35;', finalUrl);
      return finalUrl;
    }

    console.log('%c[RCA] Using direct host routing...', 'color: #FF6B35;');
    let host = '';
    switch (urlobj.key) {
      case 1: {
        host = environment.iamhost;
        break;
      }
      case 2: {
        host = environment.dcmhost;
        break;
      }
      case 3: {
        host = environment.scmhost;
        break;
      }
      default: {
        host = environment.reactivehost;
      }
    }
    const finalUrl = host + urlobj.part;
    console.log(`%c[Direct API Call] %c=> ${finalUrl}`, 'color: #0000FF; font-weight: bold;', 'color: #444;');
    console.log('%c[RCA] Returning direct URL:', 'color: #FF6B35;', finalUrl);
    return finalUrl;
  }
}